import { useState, useCallback, useRef, useEffect } from "react"

export function useAiStream() {
  const [isStreaming, setIsStreaming] = useState(false)
  const abortRef = useRef(null)
  const activeRequestIdRef = useRef(0)
  const isMountedRef = useRef(true)

  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
      abortRef.current?.abort()
    }
  }, [])

  const stream = useCallback(
    async ({
      articleId,
      prompt,
      paragraph,
      articleContent,
      chatHistory,
      onChunk,
      onDone,
      onError,
      baseUrl,
    }) => {
      // Cancel any existing stream
      if (abortRef.current) {
        abortRef.current.abort()
      }

      const requestId = ++activeRequestIdRef.current
      abortRef.current = new AbortController()
      setIsStreaming(true)

      // Frontend timeout (120s)
      const timeoutId = setTimeout(() => {
        abortRef.current?.abort()
        onError?.("Request timed out")
      }, 120_000)

      let reader = null

      try {
        const csrfToken = document.querySelector(
          'meta[name="csrf-token"]'
        )?.content

        const response = await fetch(
          `${baseUrl}/articles/${articleId}/ai_assist`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "X-CSRF-Token": csrfToken,
            },
            credentials: "same-origin",
            body: JSON.stringify({
              prompt,
              paragraph,
              article_content: articleContent,
              chat_history: chatHistory,
            }),
            signal: abortRef.current.signal,
          }
        )

        if (!response.ok) {
          onError?.("Failed to connect to AI assistant")
          return
        }

        reader = response.body.getReader()
        const decoder = new TextDecoder()
        let buffer = ""

        while (true) {
          const { done, value } = await reader.read()
          if (done) break

          // Check if this request is still active
          if (
            requestId !== activeRequestIdRef.current ||
            !isMountedRef.current
          ) {
            reader.releaseLock()
            return
          }

          buffer += decoder.decode(value, { stream: true })

          while (true) {
            const lineEnd = buffer.indexOf("\n")
            if (lineEnd === -1) break
            const line = buffer.slice(0, lineEnd).trim()
            buffer = buffer.slice(lineEnd + 1)

            if (!line || line.startsWith(":")) continue // empty or SSE comment

            if (line.startsWith("data: ")) {
              const data = line.slice(6)
              if (data === "[DONE]") {
                onDone?.()
                return
              }
              try {
                const parsed = JSON.parse(data)
                if (parsed.error) {
                  onError?.(parsed.error)
                  return
                }
                if (parsed.content) onChunk(parsed.content)
              } catch {
                // ignore parse errors
              }
            }
          }
        }

        // Stream ended without [DONE]
        onDone?.()
      } catch (err) {
        if (err.name !== "AbortError") {
          onError?.(err.message || "Connection lost")
        }
      } finally {
        clearTimeout(timeoutId)
        if (reader) {
          try {
            reader.releaseLock()
          } catch {}
        }
        if (isMountedRef.current) {
          setIsStreaming(false)
        }
      }
    },
    []
  )

  const cancel = useCallback(() => {
    abortRef.current?.abort()
    setIsStreaming(false)
  }, [])

  return { stream, cancel, isStreaming }
}
